/**
 * @fileoverview OffCanvasRoomList Component - Accessibility mirror for screen readers
 * @author MCHD Room Scheduler Team
 * @created 09-10-2025 04:30:44
 * 
 * Off-canvas list that mirrors the floor plan canvas content for screen readers.
 * Per Floor Plan Spec Section 11: Accessibility Requirements
 * 
 * Features:
 * - Hidden visually but exposed to assistive technology
 * - Mirrors canvas room selection
 * - Each room item exposes: name, capacity, state, and actions
 * - List scroll syncs selection with canvas
 * - Full keyboard navigation support
 */

import React, { useEffect, useRef } from 'react';
import { Box, Stack, Text, Group, Badge, Button } from '@mantine/core';
import {
  IconDoor,
  IconUserPlus,
  IconBan,
  IconCalendar
} from '@tabler/icons-react';

// =============================================================================
// TYPES
// =============================================================================

interface RoomForList {
  id: string;
  number: string;
  name: string;
  capacity: number;
  isActive: boolean;
  assignments: any[];
  blocks: any[];
}

interface OffCanvasRoomListProps {
  /** List of rooms to display */
  rooms: RoomForList[];
  
  /** Currently selected room */
  selectedRoomId?: string | undefined;
  
  /** Callback when room is selected */
  onSelectRoom: (roomId: string) => void;
  
  /** Callback to assign doctor */
  onAssignDoctor: (roomId: string) => void;
  
  /** Callback to create block */
  onCreateBlock: (roomId: string) => void;
  
  /** Callback to view schedule */
  onViewSchedule: (roomId: string) => void;
}

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Get room status label for screen readers
 */
function getRoomStatusLabel(room: RoomForList): string {
  if (!room.isActive) return 'Inactive';
  if (room.blocks.length > 0) return 'Blocked';
  if (room.assignments.length > 0) return 'Occupied';
  return 'Available';
}

/**
 * Get room status color
 */
function getRoomStatusColor(room: RoomForList): string {
  if (!room.isActive) return 'gray';
  if (room.blocks.length > 0) return 'orange';
  if (room.assignments.length > 0) return 'blue';
  return 'green';
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

/**
 * OffCanvasRoomList Component
 * 
 * Accessibility-focused room list that mirrors the floor plan canvas.
 * Hidden visually using CSS but fully accessible to screen readers.
 * 
 * WCAG 2.1 AA Compliance:
 * - Semantic HTML with proper ARIA labels
 * - Keyboard navigable (Tab, Enter)
 * - Focus management syncs with canvas
 * - Clear action labels and status indicators
 * 
 * Per Spec Section 11:
 * - Each list item mirrors canvas room state
 * - Actions match context menu options
 * - Selection syncs with canvas
 * 
 * @example
 * ```tsx
 * <OffCanvasRoomList
 *   rooms={floorPlanData.rooms}
 *   selectedRoomId={selectedRoom?.id}
 *   onSelectRoom={(id) => setSelectedRoom(rooms.find(r => r.id === id))}
 *   onAssignDoctor={(id) => openAssignModal(id)}
 *   onCreateBlock={(id) => openBlockModal(id)}
 *   onViewSchedule={(id) => openScheduleSidebar(id)}
 * />
 * ```
 */
export const OffCanvasRoomList: React.FC<OffCanvasRoomListProps> = ({
  rooms,
  selectedRoomId,
  onSelectRoom,
  onAssignDoctor,
  onCreateBlock,
  onViewSchedule
}) => {
  const listRef = useRef<HTMLDivElement>(null);
  const selectedItemRef = useRef<HTMLDivElement>(null);
  
  // Scroll selected item into view when selection changes
  useEffect(() => {
    if (selectedItemRef.current) {
      selectedItemRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest'
      });
    }
  }, [selectedRoomId]);
  
  return (
    <Box
      ref={listRef}
      style={{
        position: 'absolute',
        left: '-9999px',
        width: '1px',
        height: '1px',
        overflow: 'hidden'
      }}
      role="list"
      aria-label="Floor plan rooms accessible list"
    >
      {rooms.map((room) => {
        const isSelected = selectedRoomId === room.id;
        const statusLabel = getRoomStatusLabel(room);
        const statusColor = getRoomStatusColor(room);
        
        return (
          <Box
            key={room.id}
            ref={isSelected ? selectedItemRef : undefined}
            role="listitem"
            aria-label={`Room ${room.number}: ${room.name}, Capacity ${room.assignments.length} of ${room.capacity}, Status ${statusLabel}`}
            aria-current={isSelected}
            tabIndex={0}
            onClick={() => onSelectRoom(room.id)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSelectRoom(room.id);
              }
            }}
            style={{
              padding: '12px',
              border: isSelected ? '2px solid blue' : '1px solid transparent',
              marginBottom: '8px',
              borderRadius: '8px'
            }}
          >
            <Stack gap="sm">
              {/* Room header */}
              <Group justify="space-between">
                <Group gap="xs">
                  <IconDoor size={16} aria-hidden="true" />
                  <Text fw={600}>Room {room.number}</Text>
                </Group>
                <Badge color={statusColor} size="sm">
                  {statusLabel}
                </Badge>
              </Group>
              
              {/* Room name */}
              <Text size="sm" c="dimmed">{room.name}</Text>
              
              {/* Capacity */}
              <Text size="sm">
                Capacity: {room.assignments.length} of {room.capacity}
              </Text>
              
              {/* Actions */}
              <Group gap="xs">
                <Button
                  size="xs"
                  variant="light"
                  leftSection={<IconUserPlus size={14} />}
                  onClick={(e) => {
                    e.stopPropagation();
                    onAssignDoctor(room.id);
                  }}
                  disabled={!room.isActive}
                  aria-label={`Assign doctor to room ${room.number}`}
                >
                  Assign
                </Button>
                
                <Button
                  size="xs"
                  variant="light"
                  leftSection={<IconBan size={14} />}
                  onClick={(e) => {
                    e.stopPropagation();
                    onCreateBlock(room.id);
                  }}
                  aria-label={`Create block for room ${room.number}`}
                >
                  Block
                </Button>
                
                <Button
                  size="xs"
                  variant="light"
                  leftSection={<IconCalendar size={14} />}
                  onClick={(e) => {
                    e.stopPropagation();
                    onViewSchedule(room.id);
                  }}
                  aria-label={`View schedule for room ${room.number}`}
                >
                  Schedule
                </Button>
              </Group>
            </Stack>
          </Box>
        );
      })}
    </Box>
  );
};

export default OffCanvasRoomList;

